import {useMemo, useCallback} from 'react'
import {Box, Tooltip} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useAppDispatch, useAppSelector} from '../../../common/store'
import {setCameraPosition, selectActiveScene} from '../../../common/components/store/scenesSlice'
import {triggerCameraPreset} from '../../../common/components/deviceMesh/cameraPresetStore'
import {PRESETS, scalePresetPosition} from './cameraPresets'
import CameraPresetThumbnail from './CameraPresetThumbnail'
import {useAppTokens} from '../../theme'
import type {ThemeTokens} from '../../theme'

// ── CameraPresetGrid — 2x2 grid of preset thumbnails (no title) ────

const CameraPresetGrid = () => {
  const {t} = useTranslation()
  const tokens = useAppTokens()
  const dispatch = useAppDispatch()
  const deviceWidth = useAppSelector((s) => selectActiveScene(s).deviceWidth)
  const deviceHeight = useAppSelector((s) => selectActiveScene(s).deviceHeight)
  const exportWidth = useAppSelector((s) => selectActiveScene(s).exportWidth)
  const exportHeight = useAppSelector((s) => selectActiveScene(s).exportHeight)

  const handlePresetClick = useCallback(
    (presetId: string) => {
      const preset = PRESETS.find((p) => p.id === presetId)
      if (!preset) return
      const {rotation} = preset
      // Fit the preset distance to the device size + export aspect so the whole device
      // stays visible (tablets / non-phone aspects aren't over- or under-zoomed).
      const position = scalePresetPosition(preset.position, deviceWidth, deviceHeight, exportWidth, exportHeight)
      triggerCameraPreset(position, rotation)
      dispatch(setCameraPosition({position, rotation}))
    },
    [dispatch, deviceWidth, deviceHeight, exportWidth, exportHeight],
  )

  const sx = useMemo(() => buildStyles(tokens), [tokens])

  return (
    <Box sx={sx.grid}>
      {PRESETS.map((preset) => (
        <Tooltip key={preset.id} title={t(`scene.camera.preset_${preset.id}`, preset.id)} placement="top">
          <Box sx={sx.button} onClick={() => handlePresetClick(preset.id)} role="button" tabIndex={0}>
            <CameraPresetThumbnail presetId={preset.id} />
          </Box>
        </Tooltip>
      ))}
    </Box>
  )
}

const buildStyles = (tokens: ThemeTokens) => ({
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '6px',
  },
  button: {
    cursor: 'pointer',
    padding: '2px',
    borderRadius: '6px',
    border: `1px solid transparent`,
    transition: 'border-color 0.15s, background-color 0.15s',
    '&:hover': {
      borderColor: tokens.text.muted,
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
    },
  },
})

export default CameraPresetGrid
